import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { TOKEN_KEY } from './api'
import { decideLoginStep } from './authFlow'
import { decodeJwtClaims, isTokenExpired } from './authToken'
import { afterVerifyUrl } from './verifyRedirect'
import { Mark } from './brand/Icon'
import { useBrandName } from './brand/pack'
import { errorText, t as tAmbient, useT } from './i18n'

interface AuthUser {
  user_id: number
  email: string
  active_org_id?: number | null
}

interface AuthContextValue {
  token: string | null
  user: AuthUser | null
  isLoading: boolean
  setSession: (token: string, user: AuthUser) => void
  logout: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

/** Разобрать payload JWT (без проверки подписи — только для UI-состояния). */
function decodeJwtUser(token: string): AuthUser | null {
  const claims = decodeJwtClaims(token)
  if (!claims?.sub) return null
  return {
    user_id: Number(claims.sub),
    email: claims.email ?? '',
    active_org_id: claims.active_org_id ?? null,
  }
}

function readToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY)
  } catch {
    return null
  }
}

function writeToken(token: string | null) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token)
    else localStorage.removeItem(TOKEN_KEY)
  } catch {
    /* ignore */
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(readToken)
  const [user, setUser] = useState<AuthUser | null>(() => {
    const t = readToken()
    return t ? decodeJwtUser(t) : null
  })
  const [isLoading, setIsLoading] = useState(true)

  const setSession = (newToken: string, newUser: AuthUser) => {
    writeToken(newToken)
    setToken(newToken)
    setUser(newUser)
  }

  const clearSession = () => {
    writeToken(null)
    setToken(null)
    setUser(null)
  }

  const logout = () => {
    clearSession()
    fetch('/api/v1/auth/logout', { method: 'POST', credentials: 'include' }).catch(() => {})
  }

  // Глобальная реакция на 401 из cabinet API (api.ts) — сбросить сессию → LoginScreen
  useEffect(() => {
    const onUnauthorized = () => clearSession()
    window.addEventListener('cabinet:unauthorized', onUnauthorized)
    return () => window.removeEventListener('cabinet:unauthorized', onUnauthorized)
  }, [])

  // Тихий refresh в api.ts выдал новый access-токен → синхронизировать состояние
  // (токен уже записан в localStorage; здесь обновляем контекст для UI).
  useEffect(() => {
    const onRefreshed = (e: Event) => {
      const detail = (e as CustomEvent).detail as { token?: string; user?: AuthUser } | undefined
      if (!detail?.token) return
      setToken(detail.token)
      setUser(detail.user ?? decodeJwtUser(detail.token))
    }
    window.addEventListener('cabinet:token-refreshed', onRefreshed)
    return () => window.removeEventListener('cabinet:token-refreshed', onRefreshed)
  }, [])

  useEffect(() => {
    let cancelled = false

    async function bootstrap() {
      // 1. Magic-link verification (?token=... in URL)
      const params = new URLSearchParams(window.location.search)
      const magicToken = params.get('token')
      if (magicToken) {
        try {
          const res = await fetch('/api/v1/auth/verify', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ token: magicToken }),
          })
          // Адрес правится и при УДАЧЕ, и при отказе сервера: одноразовый
          // токен не должен остаться в истории браузера и уехать в закладку.
          // Сетевой обрыв (catch ниже) — другое дело: там токен ещё жив, и
          // перезагрузка страницы обязана дать вторую попытку.
          const clean = afterVerifyUrl(window.location.pathname, window.location.search)
          window.history.replaceState({}, '', clean)
          if (res.ok) {
            const data = await res.json()
            if (!cancelled) setSession(data.access_token, data.user)
            if (!cancelled) setIsLoading(false)
            return
          }
        } catch {
          /* fallthrough to refresh */
        }
      }

      // 2. Есть ВАЛИДНЫЙ (непротухший) токен — восстановить user из claims.
      //    Протухший НЕ используем (иначе первый запрос 401 → логаут) — идём в refresh.
      const existing = readToken()
      if (existing && !isTokenExpired(existing)) {
        const restored = decodeJwtUser(existing)
        if (!cancelled) {
          setUser(restored)
          setIsLoading(false)
        }
        return
      }

      // 3. Тихий refresh по httpOnly-cookie (30 дней) — прозрачно продлевает сессию.
      try {
        const res = await fetch('/api/v1/auth/refresh', { method: 'POST', credentials: 'include' })
        if (res.ok) {
          const data = await res.json()
          if (!cancelled) setSession(data.access_token, data.user)
        } else if (existing) {
          // refresh отказал, а протухший токен ещё лежит — вычистить, чтобы не зациклиться.
          writeToken(null)
          if (!cancelled) {
            setToken(null)
            setUser(null)
          }
        }
      } catch {
        /* not authenticated */
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    bootstrap()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <AuthContext.Provider value={{ token, user, isLoading, setSession, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}

type LoginPhase = 'email' | 'password' | 'sent' | 'pending'

/** Статус заявки → ключ каталога. */
export const PENDING_KEY: Record<string, string> = {
  pending: 'auth.pending.underReview',
  rejected: 'auth.error.rejected',
  active: 'auth.pending.alreadyRegistered',
}

/** Экспортирован для харнесса визуальной сверки (`demo-shell.html?screen=login`):
 *  экран входа стоит ПЕРЕД AuthGate, и на проде его иначе не снять — за гейтом
 *  уже кабинет. Двуязычный вход надо смотреть глазами, а не только по каталогу. */
export function LoginScreen() {
  const t = useT()
  // Имя бренда — из пака домена; ключ каталога остаётся запасным (см. brand/pack).
  const brandName = useBrandName()
  const { setSession } = useAuth()
  const [phase, setPhase] = useState<LoginPhase>('email')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [pendingMsg, setPendingMsg] = useState('')

  const cleanEmail = email.trim().toLowerCase()

  // Отказ сервера приходит КОДОМ, а не фразой: язык кабинета задаёт домен, и
  // готовая русская строка от бэкенда прилетела бы в английскую витрину как
  // есть. Строка в detail — старая форма, оставлена для совместимости.
  async function detailOf(res: Response, fallback: string): Promise<string> {
    try {
      const body = await res.json()
      if (body?.detail && typeof body.detail === 'object') return errorText(body.detail)
      if (typeof body?.detail === 'string') return body.detail
    } catch {
      /* non-JSON body */
    }
    return fallback
  }

  // Отправить magic-link и перейти на экран «письмо отправлено».
  async function sendMagicLink() {
    setBusy(true)
    setError(null)
    try {
      const res = await fetch('/api/v1/auth/magic-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: cleanEmail }),
      })
      if (!res.ok) {
        // 403 — новый зарубежный email (406-ФЗ): показываем текст сервера.
        setError(await detailOf(res, t('auth.error.linkFailed')))
        return
      }
      setPhase('sent')
    } catch {
      setError(t('auth.error.linkFailed'))
    } finally {
      setBusy(false)
    }
  }

  // Новый email → заявка на регистрацию (модерация админом). magic-link НЕ шлётся.
  async function register() {
    setBusy(true)
    setError(null)
    try {
      const res = await fetch('/api/v1/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: cleanEmail }),
      })
      if (!res.ok) {
        // 403 — новый зарубежный email (406-ФЗ): показываем текст сервера.
        setError(await detailOf(res, t('auth.error.registerFailed')))
        return
      }
      // Берём СТАТУС, а не готовую фразу сервера: она русская всегда.
      const data = (await res.json()) as { status: string }
      setPendingMsg(t(PENDING_KEY[data.status] ?? 'auth.pending.submitted'))
      setPhase('pending')
    } catch {
      setError(t('auth.error.registerFailed'))
    } finally {
      setBusy(false)
    }
  }

  // Шаг 1: email → /check-email → пароль / ссылка / заявка (по статусу).
  async function submitEmail(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (!cleanEmail.includes('@')) {
      setError(t('auth.error.badEmail'))
      return
    }
    setBusy(true)
    try {
      const res = await fetch('/api/v1/auth/check-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: cleanEmail }),
      })
      if (!res.ok) {
        // 403 — новый зарубежный email (406-ФЗ): показываем текст сервера.
        setError(await detailOf(res, t('auth.error.checkFailed')))
        return
      }
      const data = (await res.json()) as {
        exists: boolean
        has_password: boolean
        status?: string | null
      }
      const step = decideLoginStep(data)
      if (step === 'password') {
        setPhase('password')
      } else if (step === 'magic-link') {
        await sendMagicLink()
      } else if (step === 'register') {
        await register()
      } else if (step === 'pending') {
        setPendingMsg(t('auth.pending.underReview'))
        setPhase('pending')
      } else {
        // rejected
        setError(t('auth.error.rejected'))
      }
    } catch {
      setError(t('auth.error.checkFailed'))
    } finally {
      setBusy(false)
    }
  }

  // Шаг 2: вход по паролю. credentials:'include' — сервер ставит refresh-cookie.
  async function submitPassword(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      const res = await fetch('/api/v1/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: cleanEmail, password }),
      })
      if (!res.ok) {
        setError(await detailOf(res, t('auth.error.badCredentials')))
        return
      }
      const data = await res.json()
      setSession(data.access_token, data.user)
    } catch {
      setError(t('auth.error.loginFailed'))
    } finally {
      setBusy(false)
    }
  }

  function backToEmail() {
    setPhase('email')
    setPassword('')
    setError(null)
  }

  const inputClass =
    'w-full rounded-xl border border-hull bg-trench px-4 py-3 text-base text-foam placeholder:text-mist focus:border-sonar focus:outline-none disabled:opacity-50'
  const primaryBtnClass =
    'w-full rounded-xl bg-gradient-to-b from-sonar to-sonar-dim px-4 py-3 font-semibold text-abyss shadow-sonar transition-colors hover:from-sonar hover:to-sonar disabled:opacity-40'

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-abyss px-4">
      {/* Амбиентная водная колонка на фоне.
          Цвета — ТОКЕНАМИ, а не литералами: экран входа первое, что видит
          человек, и зашитая сине-стальная гамма открывала партнёру с зелёной
          айдентикой синий фон, а бренду «Лоция» — ровно ту синеву, от которой
          он уходит («подложка не синяя» — его правило). Сторож —
          `brand/ambient.test.ts`. */}
      <div
        className="pointer-events-none absolute inset-0 opacity-70"
        style={{
          background:
            'radial-gradient(900px 500px at 50% -10%, rgb(var(--accent) / .12), transparent 60%), linear-gradient(180deg,rgb(var(--surface-page)) 0%,rgb(var(--surface-raised)) 100%)',
        }}
        aria-hidden
      />
      <div className="relative w-full max-w-sm">
        <div className="mb-8 text-center">
          <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-2xl border border-hull bg-trench text-foam">
            <Mark className="h-9 w-9" />
          </div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-foam">
            {t('auth.title')}
          </h1>
          <p className="eyebrow mt-2 justify-center">{brandName}</p>
        </div>

        {phase === 'sent' ? (
          <div className="rounded-xl border border-sonar/30 bg-sonar/10 p-5 text-center text-sm text-foam">
            {t('auth.sent.before')} <b className="text-sonar">{cleanEmail}</b>
            {t('auth.sent.after')}
          </div>
        ) : phase === 'pending' ? (
          <div className="rounded-xl border border-sonar/30 bg-sonar/10 p-5 text-center text-sm text-foam">
            <p className="leading-relaxed">{pendingMsg}</p>
            <p className="mt-2 text-mist">
              {t('auth.pending.forEmail')} <b className="text-foam">{cleanEmail}</b>
            </p>
            <button
              type="button"
              onClick={backToEmail}
              className="mt-3 text-sm text-mist hover:text-foam"
            >
              {t('auth.otherEmail')}
            </button>
          </div>
        ) : phase === 'password' ? (
          <form onSubmit={submitPassword} className="space-y-4">
            {/* readonly email — чтобы менеджеры паролей связали логин с паролем */}
            <input type="email" value={cleanEmail} readOnly autoComplete="username" hidden />
            <div>
              <label htmlFor="password" className="eyebrow mb-2">
                {t('auth.password')}
              </label>
              <p className="mb-2 px-1 text-sm text-mist">
                {t('auth.forEmail')} <b className="text-foam">{cleanEmail}</b>
              </p>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                placeholder={t('auth.passwordPlaceholder')}
                value={password}
                onChange={e => {
                  setPassword(e.target.value)
                  setError(null)
                }}
                disabled={busy}
                autoFocus
                className={inputClass}
              />
            </div>
            {error && <p className="px-1 text-sm leading-relaxed text-coral">{error}</p>}
            <button type="submit" disabled={busy || !password} className={primaryBtnClass}>
              {busy ? t('auth.signingIn') : t('auth.signIn')}
            </button>
            <div className="flex items-center justify-between px-1 text-sm">
              <button
                type="button"
                onClick={sendMagicLink}
                disabled={busy}
                className="text-sonar hover:underline disabled:opacity-40"
              >
                {t('auth.signInByLink')}
              </button>
              <button
                type="button"
                onClick={backToEmail}
                disabled={busy}
                className="text-mist hover:text-foam disabled:opacity-40"
              >
                {t('auth.otherEmail')}
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={submitEmail} className="space-y-4">
            <div>
              <label htmlFor="email" className="eyebrow mb-2">
                Email
              </label>
              <input
                id="email"
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="your@email.com"
                value={email}
                onChange={e => {
                  setEmail(e.target.value)
                  setError(null)
                }}
                disabled={busy}
                className={inputClass}
              />
            </div>
            {error && <p className="px-1 text-sm leading-relaxed text-coral">{error}</p>}
            <button type="submit" disabled={busy || !email.trim()} className={primaryBtnClass}>
              {busy ? t('auth.checking') : t('auth.next')}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}

export function AuthGate({ children }: { children: ReactNode }) {
  const { token, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center text-slate-400">
        {tAmbient('common.loading')}
      </div>
    )
  }
  if (!token) return <LoginScreen />
  return <>{children}</>
}
